import { IColumnSelectionState, ITableColumn } from './types';

const getLocalStorageKey = (storageKey: string) =>
  `table-columns-${storageKey}`;

export const storeCustomColumns = (
  columns: IColumnSelectionState[],
  storageKey: string
) => {
  localStorage.setItem(getLocalStorageKey(storageKey), JSON.stringify(columns));
};

export const retrieveCustomColumns = (
  allColumns: ITableColumn[],
  storageKey: string
): IColumnSelectionState[] => {
  const storedAccessors: string | null = localStorage.getItem(
    getLocalStorageKey(storageKey)
  );

  const accessors: IColumnSelectionState[] = storedAccessors
    ? JSON.parse(storedAccessors)
    : null;

  const needsDefaults: boolean =
    !storedAccessors || reconcileColumns(allColumns, accessors);

  if (needsDefaults) {
    const defaultColumns: IColumnSelectionState[] =
      getDefaultColumns(allColumns);

    storeCustomColumns(defaultColumns, storageKey);

    return defaultColumns;
  }

  return accessors;
};

export const reconcileColumns = (
  allColumns: ITableColumn[],
  accessors: IColumnSelectionState[]
): boolean => {
  const availableAccessorsStrings: string[] = allColumns.map(
    (column: ITableColumn) => {
      return column.accessor;
    }
  );

  const filteredAvailableAccessorsStrings: string[] =
    availableAccessorsStrings.filter((accessor: string | undefined) => {
      if (accessor === undefined || accessor === '') {
        return false;
      } else {
        return true;
      }
    });

  const currentColumnAccessorsInTableSet = new Set<string>(
    filteredAvailableAccessorsStrings
  );

  const missingAccessors: IColumnSelectionState[] = accessors.filter(
    (accessorObj: IColumnSelectionState) => {
      if (accessorObj.accessor === undefined || accessorObj.accessor === '') {
        return true;
      }

      if (currentColumnAccessorsInTableSet.has(accessorObj.accessor)) {
        return false;
      } else {
        return true;
      }
    }
  );

  if (missingAccessors.length > 0) {
    return true;
  }

  return false;
};

export const getDefaultColumns = (
  allColumns: ITableColumn[]
): IColumnSelectionState[] => {
  return allColumns
    .map((column: ITableColumn, index: number) => {
      return {
        accessor: column.accessor,
        position: index,
        selected:
          column.columnDefaultSelection ||
          false ||
          !!column.columnSelectionDisabled,
      };
    })
    .filter(Boolean) as IColumnSelectionState[];
};

export const transformColumnSelections = (
  allColumns: ITableColumn[],
  columnSelections: IColumnSelectionState[]
): ITableColumn[] => {
  const transformedColumns: ITableColumn[] = allColumns
    .reduce((acc: ITableColumn[], column: ITableColumn) => {
      const columnSelection: IColumnSelectionState = columnSelections.find(
        (selection: IColumnSelectionState) =>
          selection.accessor === column.accessor
      );

      if (columnSelection?.selected) {
        acc[columnSelection.position] = column;
      }

      return acc;
    }, [] as ITableColumn[])
    .filter(Boolean);

  return transformedColumns;
};
